/**
 * TocSidebar — React-DOM table-of-contents sidebar for the Arianna reader.
 *
 * Design source of truth: apps/reader/src/screens/ManualScreen.tsx (TocSidebar /
 * TocParent / TocSection). Props are platform-agnostic (no Inertia/Tauri deps).
 *
 * DS primitives used: Input, Pill, Text (web variants).
 * reader-core functions used: isSectionCompleted, isSectionForReview,
 *   getTocParentFullLabel, getTocSectionFullLabel.
 */

import * as React from 'react'
import { useMemo, useState } from 'react'
import { clsx } from 'clsx'
import {
  isSectionCompleted,
  isSectionForReview,
  getTocParentLabel,
  getTocParentPrefix,
  getTocSectionDisplayId,
  type ReaderRouteSection,
  type ReaderRouteParent,
} from '@arianna/reader-core'
import Pill from '@arianna/design-system/Pill'
import Text from '@arianna/design-system/Text'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TocSidebarProps {
  /**
   * Ordered list of all indexable sections from getIndexableSections().
   * Each section carries parentId / parentTitle / parentNumericLabel.
   */
  sections: ReaderRouteSection[]
  /**
   * Ordered list of parents (chapters + optional compendio) from
   * getRouteParents(). Required to drive accordion order and TocParent labels.
   */
  parents: ReaderRouteParent[]
  /** Flat index (into sections[]) of the currently displayed section. */
  currentIdx: number
  /** Set of subParaIds that have been marked as completed. */
  completedIds: ReadonlySet<string>
  /** Set of subParaIds that have been flagged for review. */
  reviewIds: ReadonlySet<string>
  /** Called when the user selects a section — receives the flat index. */
  onSelect: (idx: number) => void
  /** Optional additional CSS class for the root <aside>. */
  className?: string
}

// ---------------------------------------------------------------------------
// Internal derived types
// ---------------------------------------------------------------------------

interface TocGroup {
  parent: ReaderRouteParent
  parentId: string
  items: Array<{ section: ReaderRouteSection; index: number }>
}

// ---------------------------------------------------------------------------
// TocSidebar
// ---------------------------------------------------------------------------

/**
 * Accordion TOC sidebar. Mirrors the iPad TocSidebar behaviour exactly:
 *   - Eyebrow "INDICE" + completed/total counter
 *   - Search input
 *   - Filter pills: Tutti / Da rivedere / Non letti
 *   - Accordion per chapter/compendio with progress bar
 *   - Section rows: label via getTocSectionFullLabel, ✓ completed, gold dot review,
 *     active indicator bar on the left
 *
 * All state is internal; the parent only supplies the data + onSelect callback.
 */
export function TocSidebar({
  sections,
  parents,
  currentIdx,
  completedIds,
  reviewIds,
  onSelect,
  className,
}: TocSidebarProps) {
  // Build ordered groups from parents (preserves compendio-first order).
  // Each group lists sections whose parentId matches the parent.
  const groups = useMemo<TocGroup[]>(() => {
    return parents.map((parent) => {
      const parentId = parent.kind === 'compendio' ? 'compendio' : parent.node.id
      const items: TocGroup['items'] = []
      for (let i = 0; i < sections.length; i++) {
        const s = sections[i]
        if (s && s.parentId === parentId) {
          items.push({ section: s, index: i })
        }
      }
      return { parent, parentId, items }
    })
  }, [parents, sections])

  // Which parentId does the current section belong to?
  const currentParentId = sections[currentIdx]?.parentId ?? null

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'review' | 'unread'>('all')
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    () => new Set(currentParentId != null ? [currentParentId] : []),
  )

  // Auto-expand the parent of the current section when it changes via
  // prev/next navigation (pure memo — no useEffect setState anti-pattern).
  const expandedWithCurrent = useMemo<ReadonlySet<string>>(() => {
    if (currentParentId == null || expandedGroupIds.has(currentParentId)) return expandedGroupIds
    const next = new Set(expandedGroupIds)
    next.add(currentParentId)
    return next
  }, [currentParentId, expandedGroupIds])

  const handleExpandToggle = (parentId: string) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(parentId)) {
        next.delete(parentId)
      } else {
        next.add(parentId)
      }
      return next
    })
  }

  // Apply filter + search within each group.
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase()
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter(({ section: s }) => {
          if (filter === 'review' && !isSectionForReview(s, reviewIds)) return false
          if (filter === 'unread' && isSectionCompleted(s, completedIds)) return false
          if (q.length === 0) return true
          return (
            s.node.title.toLowerCase().includes(q) ||
            s.parentTitle.toLowerCase().includes(q) ||
            s.id.toLowerCase().includes(q)
          )
        }),
      }))
      .filter((g) => g.items.length > 0)
  }, [groups, filter, search, completedIds, reviewIds])

  // Overall completed-section count (mirrors iPad header counter).
  const completedSectionCount = useMemo(
    () => sections.filter((s) => isSectionCompleted(s, completedIds)).length,
    [sections, completedIds],
  )

  // Total section count (denominator for footer & header counter).
  const totalSections = sections.length
  // Pace estimator: assumes ~3 sections/day reading; integer days remaining.
  // Pure presentation — does not need to be exact, just narrative-grade.
  const remainingDays = Math.max(1, Math.ceil((totalSections - completedSectionCount) / 3))
  const progressPct = totalSections === 0 ? 0 : (completedSectionCount / totalSections) * 100

  return (
    <aside
      className={clsx(
        /* design handoff: fixed 280px sidebar width */
        'w-[280px] shrink-0',
        'border-r border-line',
        'bg-bg-soft',
        'flex flex-col h-full',
        className,
      )}
      /* paper-noise is a CSS var containing a data-URI SVG — no Tailwind utility for backgroundImage */
      style={{ backgroundImage: 'var(--paper-noise)' }}
    >
      {/* ── Header: eyebrow "INDICE" + mono counter ── */}
      {/* design handoff: 16px top/sides, 10px bottom — 10px is off the 4px scale (singoletto) */}
      <div className="pt-4 px-4 pb-[10px] flex items-center justify-between">
        {/* design handoff: 10px eyebrow — below text-xs (11px); singoletto */}
        <span className="text-[10px] text-ink-3 font-sans font-semibold uppercase tracking-[0.12em]">
          Indice
        </span>
        {/* design handoff: 10px mono counter; fontFeatureSettings not a Tailwind utility */}
        <span
          className="text-[10px] text-ink-3 font-mono"
          style={{ fontFeatureSettings: '"ss01"' }}
        >
          {completedSectionCount}/{totalSections}
        </span>
      </div>

      {/* ── Search input (inline with icon, matches design handoff) ── */}
      <div className="px-3 pb-2">
        {/* design handoff: gap 7px and padding 6px 10px are off-scale singoletti */}
        <div className="flex items-center gap-[7px] px-[10px] py-[6px] bg-surface border border-line rounded-md">
          <svg
            width="12"
            height="12"
            viewBox="0 0 22 22"
            fill="none"
            className="text-ink-3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="9.5" cy="9.5" r="5.5" />
            <line x1="13.5" y1="13.5" x2="18" y2="18" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca paragrafo…"
            /* design handoff: 12px — between text-xs (11px) and text-sm (13px); singoletto */
            className="text-[12px] border-none bg-transparent outline-none text-ink flex-1 min-w-0 font-[inherit]"
          />
        </div>
      </div>

      {/* ── Filter pills ── */}
      {/* design handoff: 11px font, 4px/10px padding — off-scale singoletti */}
      <div className="flex flex-wrap gap-1 px-3 pb-[10px]">
        <Pill
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          className="text-[11px] py-1 px-[10px]"
        >
          Tutto
        </Pill>
        <Pill
          active={filter === 'review'}
          onClick={() => setFilter('review')}
          className="text-[11px] py-1 px-[10px]"
        >
          Rivedere
        </Pill>
        <Pill
          active={filter === 'unread'}
          onClick={() => setFilter('unread')}
          className="text-[11px] py-1 px-[10px]"
        >
          Non letti
        </Pill>
      </div>

      {/* ── Accordion list ── */}
      {/* design handoff: 6px horizontal padding, 12px bottom — off-scale singoletti */}
      <div className="flex-1 overflow-y-auto pt-0 px-[6px] pb-3">
        {filteredGroups.map((g) => (
          <TocGroupItem
            key={g.parentId}
            group={g}
            currentIdx={currentIdx}
            completedIds={completedIds}
            reviewIds={reviewIds}
            expanded={expandedWithCurrent.has(g.parentId)}
            onExpandToggle={() => handleExpandToggle(g.parentId)}
            onSelect={onSelect}
          />
        ))}

        {filteredGroups.length === 0 && (
          <div className="py-6 px-4 text-center">
            {/* design handoff: 12px — singoletto between text-xs (11px) and text-sm (13px) */}
            <Text variant="meta" className="text-[12px] text-ink-3">
              Nessun paragrafo trovato
            </Text>
          </div>
        )}
      </div>

      {/* ── Footer card: "A che punto sei" — design handoff parity ── */}
      {/* design handoff: 14px margin and padding — off-scale singoletti; 10px radius — singoletto */}
      {totalSections > 0 && (
        <div className="bg-surface border border-line mx-[14px] mb-[14px] px-[14px] py-3 rounded-[10px]">
          {/* design handoff: 9.5px eyebrow repeated in footer and section rows (see TocGroupItem,
              TocSectionRow) — appears ≥ 3× across shared components; candidate for tokenization */}
          <div className="text-[9.5px] mb-1.5 font-sans font-semibold uppercase text-ink-3 tracking-[0.1em]">
            A che punto sei
          </div>
          {/* design handoff: 13.5px — between text-sm (13px) and text-base (14px); singoletto */}
          <div className="text-[13.5px] leading-[1.35] tracking-[-0.005em] font-serif text-ink">
            {completedSectionCount >= totalSections ? (
              <>
                Manuale <strong>completato</strong>.
              </>
            ) : (
              <>
                A questo ritmo, finisci il manuale{' '}
                <strong>
                  tra {remainingDays} {remainingDays === 1 ? 'giorno' : 'giorni'}
                </strong>
                .
              </>
            )}
          </div>
          {/* Progress bar track */}
          <div className="h-[3px] rounded-[2px] mt-[10px] bg-surface-3 overflow-hidden">
            <div
              className="h-full bg-ar-accent transition-[width] duration-[400ms] ease-[ease]"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}
    </aside>
  )
}

// ---------------------------------------------------------------------------
// TocGroupItem — accordion row for a chapter or compendio
// ---------------------------------------------------------------------------

interface TocGroupItemProps {
  group: TocGroup
  currentIdx: number
  completedIds: ReadonlySet<string>
  reviewIds: ReadonlySet<string>
  expanded: boolean
  onExpandToggle: () => void
  onSelect: (idx: number) => void
}

function TocGroupItem({
  group: g,
  currentIdx,
  completedIds,
  reviewIds,
  expanded,
  onExpandToggle,
  onSelect,
}: TocGroupItemProps) {
  // Progress counters for this parent group.
  const totalInGroup = g.items.length
  const completedInGroup = g.items.filter(({ section: s }) =>
    isSectionCompleted(s, completedIds),
  ).length
  const groupDone = completedInGroup === totalInGroup && totalInGroup > 0
  const pct = totalInGroup === 0 ? 0 : completedInGroup / totalInGroup

  // Full label — mirrors iPad TocParent: chapter → "{id}  {title}", compendio → "Compendio"
  return (
    <div className="mb-[2px]">
      {/* Group header — clickable to expand/collapse */}
      <button
        onClick={onExpandToggle}
        aria-expanded={expanded}
        className={clsx(
          'w-full text-left rounded-sm p-2 px-3 cursor-pointer mb-[2px]',
          expanded ? 'bg-surface border border-line' : 'bg-transparent border border-transparent',
        )}
      >
        <div className="flex items-center gap-2">
          {/* Number column (muted, mono) — chapter prefix, or ✓ when done.
              Mirrors design handoff: the number is a separate muted mono column,
              NOT merged into the high-contrast sans title. */}
          {/* design handoff: 10px mono counter; fontFeatureSettings not a Tailwind utility */}
          <span
            className={clsx(
              'w-4 shrink-0 text-[10px] font-mono font-semibold',
              groupDone ? 'text-green' : 'text-ink-4',
            )}
            style={{ fontFeatureSettings: '"ss01"' }}
          >
            {groupDone ? '✓' : getTocParentPrefix(g.parent)}
          </span>
          {/* design handoff: 12.5px — between text-xs (11px) and text-sm (13px); singoletto
              repeated in TocGroupItem; candidate for tokenization if ≥3 occurrences confirmed */}
          <span
            className={clsx(
              'flex-1 min-w-0 text-[12.5px] tracking-[-0.005em] font-sans truncate',
              groupDone ? 'text-ink-3' : 'text-ink',
              expanded ? 'font-semibold' : 'font-medium',
            )}
          >
            {getTocParentLabel(g.parent)}
          </span>
          {/* design handoff: 9.5px mono counter (see also footer eyebrow, TocSectionRow) */}
          <span
            className="shrink-0 text-[9.5px] font-mono text-ink-4"
            style={{ fontFeatureSettings: '"ss01"' }}
          >
            {completedInGroup}/{totalInGroup}
          </span>
        </div>

        {/* Progress bar — mirrors iPad TocParent */}
        {/* design handoff: 26px left margin aligns with title column after number gutter */}
        {totalInGroup > 0 && (
          <div className="h-[2px] mt-1.5 ml-[26px] rounded-[1px] bg-surface-3 overflow-hidden">
            <div className="h-full bg-ar-accent" style={{ width: `${pct * 100}%` }} />
          </div>
        )}
      </button>

      {/* Section rows — visible when expanded */}
      {/* design handoff: 26px left margin aligns section rows with title column */}
      {expanded && (
        <div className="ml-[26px] mt-0.5 mb-1 pl-[10px] border-l border-line">
          {g.items.map(({ section: s, index }) => (
            <TocSectionRow
              key={s.id}
              section={s}
              index={index}
              isCurrent={index === currentIdx}
              completedIds={completedIds}
              reviewIds={reviewIds}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// TocSectionRow — individual section item inside an expanded accordion
// ---------------------------------------------------------------------------

interface TocSectionRowProps {
  section: ReaderRouteSection
  index: number
  isCurrent: boolean
  completedIds: ReadonlySet<string>
  reviewIds: ReadonlySet<string>
  onSelect: (idx: number) => void
}

function TocSectionRow({
  section: s,
  index,
  isCurrent,
  completedIds,
  reviewIds,
  onSelect,
}: TocSectionRowProps) {
  const completed = isSectionCompleted(s, completedIds)
  const review = isSectionForReview(s, reviewIds)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(index)
      }}
      aria-current={isCurrent ? 'true' : undefined}
      className={clsx(
        /* design handoff: 5px radius — between radius-sm (4px) and radius-md (8px); singoletto */
        'relative py-[6px] pl-3 pr-2 mb-[1px] cursor-pointer rounded-[5px]',
        isCurrent ? 'bg-[color-mix(in_oklab,var(--ar-accent)_8%,transparent)]' : 'bg-transparent',
      )}
    >
      {/* Active indicator — vertical bar on the left (mirrors iPad TocSection) */}
      {/* design handoff: -11px left positions bar just outside the paddingLeft border */}
      {isCurrent && (
        <div className="absolute left-[-11px] top-[5px] bottom-[5px] w-[2px] rounded-[2px] bg-ar-accent" />
      )}
      <div className="flex items-baseline gap-[6px]">
        {/* Number column (muted, mono, fixed width) — design handoff: the
            "1.1" / "1.kp" id lives in a separate muted mono column. */}
        {/* design handoff: 9.5px repeated in section number column, group counter, footer eyebrow
            — appears ≥3× across TocSidebar; candidate for tokenization as --text-2xs */}
        {getTocSectionDisplayId(s) !== '' && (
          <span
            className="w-[26px] shrink-0 text-[9.5px] text-ink-4 font-mono"
            style={{ fontFeatureSettings: '"ss01"' }}
          >
            {getTocSectionDisplayId(s)}
          </span>
        )}
        {/* design handoff: 11.5px — between text-xs (11px) and text-sm (13px); singoletto */}
        <span
          className={clsx(
            'flex-1 text-[11.5px] leading-[1.35] tracking-[-0.003em] font-sans',
            completed ? 'text-ink-3' : review ? 'text-gold' : isCurrent ? 'text-ink' : 'text-ink-2',
            isCurrent ? 'font-semibold' : 'font-normal',
          )}
        >
          {s.node.title}
        </span>
        {/* ✓ for completed sections */}
        {completed && <span className="text-[10px] shrink-0 text-ink-4">✓</span>}
        {/* Gold dot for review (only when not completed — mirrors iPad TocSection) */}
        {review && !completed && (
          <div className="size-1.5 rounded-full bg-gold shrink-0 self-center" />
        )}
      </div>
    </div>
  )
}
